/**
 * WizardStepProjectBasics (Development Project Step 3)
 *
 * Collects project name and address for development projects.
 * Reuses address components and jurisdiction lookup from WizardStepBasics.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, MapPin, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useLookupJurisdiction, useFloridaCounties } from '@/hooks/useCompliance';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { WizardProjectData, WizardBasicsData } from '@/types/wizard';

interface WizardStepProjectBasicsProps {
  projectData: WizardProjectData;
  basicsData: WizardBasicsData;
  onUpdateProject: (data: Partial<WizardProjectData>) => void;
  onUpdateBasics: (data: Partial<WizardBasicsData>) => void;
}

export function WizardStepProjectBasics({
  projectData,
  basicsData,
  onUpdateProject,
  onUpdateBasics,
}: WizardStepProjectBasicsProps) {
  const [streetAddress, setStreetAddress] = useState(basicsData.address?.street || '');
  const [city, setCity] = useState(basicsData.address?.city || '');
  const [zip, setZip] = useState(basicsData.address?.zip || '');
  const [selectedCounty, setSelectedCounty] = useState(basicsData.address?.county || '');

  const { data: counties = [], isLoading: countiesLoading } = useFloridaCounties();
  const lookupJurisdiction = useLookupJurisdiction();

  const lookupRef = useRef(lookupJurisdiction);
  lookupRef.current = lookupJurisdiction;

  const lastLookupKey = useRef<string>('');

  // Lookup jurisdiction when address changes
  const handleAddressLookup = useCallback(async () => {
    if (!streetAddress || !city || !selectedCounty) return;

    const lookupKey = `${streetAddress}-${city}-${selectedCounty}`;
    if (lookupKey === lastLookupKey.current) return;
    lastLookupKey.current = lookupKey;

    const fullAddress = `${streetAddress}, ${city}, FL ${zip}`.trim();

    try {
      const result = await lookupRef.current.mutateAsync({
        address: fullAddress,
        county: selectedCounty,
        city: city,
        state: 'FL',
      });

      if (result.jurisdiction) {
        onUpdateBasics({
          address: {
            street: streetAddress,
            city: city,
            state: 'FL',
            zip: zip,
            county: selectedCounty,
            formatted: fullAddress,
          },
          jurisdictionId: result.jurisdiction.id,
          jurisdictionCode: result.jurisdiction.code,
          jurisdictionName: result.jurisdiction.ahjName,
          isHvhz: result.jurisdiction.isHvhz || false,
          isFloodZone: result.jurisdiction.isFloodZone || false,
          isCoastalZone: result.jurisdiction.isCoastalZone || false,
        });
      }
    } catch (error) {
      console.error('Jurisdiction lookup failed:', error);
    }
  }, [streetAddress, city, zip, selectedCounty, onUpdateBasics]);

  // Trigger lookup when county changes
  useEffect(() => {
    if (streetAddress && city && selectedCounty) {
      const timeoutId = setTimeout(handleAddressLookup, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedCounty, streetAddress, city, handleAddressLookup]);

  // Get development type label for display
  const getDevelopmentTypeLabel = () => {
    const labels: Record<string, string> = {
      subdivision: 'Subdivision',
      townhome: 'Townhome Community',
      apartment: 'Apartment Complex',
      mixed_use: 'Mixed-Use Development',
      commercial_complex: 'Commercial Complex',
    };
    return projectData.developmentType
      ? labels[projectData.developmentType] || projectData.developmentType
      : 'Development';
  };

  return (
    <div className="space-y-6">
      {/* Question */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">Tell us about your project</h2>
        <p className="text-muted-foreground">
          Name and location for your {getDevelopmentTypeLabel().toLowerCase()}
        </p>
      </div>

      {/* Project Name */}
      <div className="space-y-2">
        <Label htmlFor="projectName">Project Name *</Label>
        <Input
          id="projectName"
          placeholder="e.g., Oakwood Estates, Riverside Townhomes"
          value={projectData.name}
          onChange={(e) => onUpdateProject({ name: e.target.value })}
        />
        <p className="text-sm text-muted-foreground">
          A name to identify this development project
        </p>
      </div>

      {/* Project Description (optional) */}
      <div className="space-y-2">
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          placeholder="Brief description of the project..."
          value={projectData.description}
          onChange={(e) => onUpdateProject({ description: e.target.value })}
          rows={2}
        />
      </div>

      {/* Address Section */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <Label className="text-base font-medium">Project Location</Label>
          </div>

          {/* Street Address */}
          <div className="space-y-2">
            <Label htmlFor="street">Street Address *</Label>
            <Input
              id="street"
              placeholder="123 Main Street or Project Site Address"
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
            />
          </div>

          {/* City and Zip */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                placeholder="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP Code</Label>
              <Input
                id="zip"
                placeholder="ZIP"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>
          </div>

          {/* County */}
          <div className="space-y-2">
            <Label htmlFor="county">County *</Label>
            <Select value={selectedCounty} onValueChange={setSelectedCounty}>
              <SelectTrigger>
                <SelectValue placeholder={countiesLoading ? 'Loading...' : 'Select county'} />
              </SelectTrigger>
              <SelectContent>
                {counties.map((county) => (
                  <SelectItem key={county.id} value={county.name}>
                    {county.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Jurisdiction Result */}
          {lookupJurisdiction.isPending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Looking up jurisdiction...
            </div>
          )}

          {basicsData.jurisdictionName && (
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-800 dark:text-green-200">
                  {basicsData.jurisdictionName}
                </span>
              </div>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                {basicsData.address?.county} County, FL
              </p>
              {/* Zone badges */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {basicsData.isHvhz && (
                  <Badge variant="secondary" className="text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    HVHZ
                  </Badge>
                )}
                {basicsData.isFloodZone && (
                  <Badge variant="secondary" className="text-xs">
                    Flood Zone
                  </Badge>
                )}
                {basicsData.isCoastalZone && (
                  <Badge variant="secondary" className="text-xs">
                    Coastal Zone
                  </Badge>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
